"use client";

import React from "react";
import { Icon, IconName } from "@zapier/zinnia-icons";
import classNames from "classnames";
import styles from "./SocialLinks.module.css";

export type SocialLinks = {
  linkedin?: string;
  twitter?: string;
  facebook?: string;
  instagram?: string;
};

export type SocialLinksProps = {
  /** Social media links for the person/brand */
  socialLinks: SocialLinks;
  /** Name of the person/brand for accessible aria-labels */
  authorName: string;
  /** Test ID for testing */
  testId?: string;
};

const SOCIAL_PLATFORMS: Record<
  keyof SocialLinks,
  { icon: IconName; label: string }
> = {
  linkedin: { icon: "socialLinkedIn", label: "LinkedIn" },
  instagram: { icon: "socialInstagram", label: "Instagram" },
  facebook: { icon: "socialFacebook", label: "Facebook" },
  twitter: { icon: "socialX", label: "X (Twitter)" },
};

/**
 * SocialLinks component that displays social media icons with accessible links.
 * Supports LinkedIn, Twitter/X, Facebook, and Instagram.
 */
export const SocialLinks = ({
  socialLinks,
  authorName,
  testId,
}: SocialLinksProps) => {
  const hasAnyLinks = Object.values(socialLinks).some(Boolean);

  if (!hasAnyLinks) {
    return null;
  }

  return (
    <div className={classNames(styles.socialLinks)} data-testid={testId}>
      {Object.entries(SOCIAL_PLATFORMS).map(([key, { icon, label }]) => {
        const url = socialLinks[key as keyof SocialLinks];
        if (!url) return null;

        return (
          <a
            key={key}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${authorName} on ${label}`}
            title={label}
            className={styles.socialLink}
            data-testid={`social-${key}`}
          >
            <Icon name={icon} size="16" />
          </a>
        );
      })}
    </div>
  );
};
